/** 会话（Conversation）仓储：CRUD + 按更新时间倒序分页。 */

import type { Table } from 'dexie';

import { getDb } from '../db';
import { newId } from '../ids';
import type { Conversation } from '../types';
import { normalizeLimit, type Page, type PageQuery } from './pagination';

export interface CreateConversationInput {
  id?: string;
  title?: string;
  createdAt?: number;
  updatedAt?: number;
  presetId?: string;
  providerId?: string;
  model?: string;
  modelKind?: Conversation['modelKind'];
  settings: Conversation['settings'];
  messageCount?: number;
}

function table(): Table<Conversation, string> {
  return getDb().conversations;
}

export function createConversation(input: CreateConversationInput): Conversation {
  const now = Date.now();
  return {
    id: input.id ?? newId(),
    title: input.title ?? '新对话',
    createdAt: input.createdAt ?? now,
    updatedAt: input.updatedAt ?? input.createdAt ?? now,
    settings: input.settings,
    messageCount: input.messageCount ?? 0,
    ...(input.presetId !== undefined ? { presetId: input.presetId } : {}),
    ...(input.providerId !== undefined ? { providerId: input.providerId } : {}),
    ...(input.model !== undefined ? { model: input.model } : {}),
    ...(input.modelKind !== undefined ? { modelKind: input.modelKind } : {}),
  };
}

/** 新增或按 id 覆盖保存（幂等）。 */
export async function putConversation(conversation: Conversation): Promise<string> {
  return table().put(conversation);
}

export async function createAndSaveConversation(input: CreateConversationInput): Promise<Conversation> {
  const conversation = createConversation(input);
  await putConversation(conversation);
  return conversation;
}

export async function getConversation(id: string): Promise<Conversation | undefined> {
  return table().get(id);
}

/** 全部会话，按 `updatedAt` 倒序（同刻用 id 倒序，保证稳定）。 */
export async function listConversations(): Promise<Conversation[]> {
  const all = await table().toArray();
  return all.sort(compareConversations);
}

function compareConversations(a: Conversation, b: Conversation): number {
  return b.updatedAt - a.updatedAt || compareDesc(a.id, b.id);
}

function compareDesc(a: string, b: string): number {
  return a < b ? 1 : a > b ? -1 : 0;
}

/** 按 `updatedAt` 倒序分页（游标格式 `${updatedAt}:${id}`，与排序规则一致）。 */
export async function listConversationsPage(query: PageQuery = {}): Promise<Page<Conversation>> {
  const limit = normalizeLimit(query.limit);
  const all = await listConversations();
  const cursor = query.cursor ? decodeTimeCursor(query.cursor) : null;

  const ordered = cursor
    ? all.filter((c) => isAfterCursor(c, cursor))
    : all;

  const items = ordered.slice(0, limit);
  const hasMore = ordered.length > limit;
  const last = items[items.length - 1];
  return {
    items,
    hasMore,
    nextCursor: hasMore && last ? `${last.updatedAt}:${last.id}` : null,
  };
}

function decodeTimeCursor(cursor: string): { updatedAt: number; id: string } | null {
  const sep = cursor.indexOf(':');
  if (sep <= 0) return null;
  const updatedAt = Number(cursor.slice(0, sep));
  if (!Number.isFinite(updatedAt)) return null;
  return { updatedAt, id: cursor.slice(sep + 1) };
}

function isAfterCursor(conversation: Conversation, cursor: { updatedAt: number; id: string }): boolean {
  return (
    conversation.updatedAt < cursor.updatedAt ||
    (conversation.updatedAt === cursor.updatedAt && conversation.id < cursor.id)
  );
}

export async function updateConversation(
  id: string,
  patch: Partial<Omit<Conversation, 'id'>>,
): Promise<Conversation | undefined> {
  const db = getDb();
  let next: Conversation | undefined;
  // 读取和写入放在同一个写事务里，避免「选模型」和「选角色卡」几乎同时
  // 落库时互相覆盖：两边都先读到旧对象、再整行写回，后写者会丢掉前一个字段。
  await db.transaction('rw', db.conversations, async () => {
    const existing = await db.conversations.get(id);
    if (!existing) return;
    next = { ...existing, ...patch, id, updatedAt: patch.updatedAt ?? Date.now() };
    await db.conversations.put(next);
  });
  return next;
}

/** 维护会话消息计数（delta 可为负，下限 0）。 */
export async function bumpMessageCount(id: string, delta = 1): Promise<Conversation | undefined> {
  const existing = await getConversation(id);
  if (!existing) return undefined;
  const messageCount = Math.max(0, existing.messageCount + delta);
  return updateConversation(id, { messageCount });
}

/** 删除会话，同时级联删除其全部消息（同一事务）。 */
export async function deleteConversation(id: string): Promise<void> {
  const db = getDb();
  await db.transaction('rw', db.conversations, db.messages, async () => {
    await db.messages.where('conversationId').equals(id).delete();
    await db.conversations.delete(id);
  });
}

export async function countConversations(): Promise<number> {
  return table().count();
}

export async function clearConversations(): Promise<void> {
  await table().clear();
}
